import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/apiClient";
import { toast } from "sonner";
import {
  Globe, ArrowRight, RefreshCw, X, MousePointerClick, Type, ChevronDown, ChevronUp,
  ArrowLeft, ArrowDown, Loader2, PowerOff,
} from "lucide-react";

const POLL_MS = 3500;

export function BrowserPreview({ open, onClose }) {
  const [state, setState] = useState({ active: false });
  const [url, setUrl] = useState("");
  const [selector, setSelector] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [lastNote, setLastNote] = useState("");
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/browser/state?screenshot=1");
      setState(data);
    } catch (_) {
      setState({ active: false });
    }
  }, []);

  useEffect(() => {
    if (!open) {
      clearInterval(timer.current);
      return;
    }
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [open, refresh]);

  const run = useCallback(
    async (action, params = {}) => {
      setBusy(true);
      try {
        const { data } = await api.post("/browser/action", { action, params });
        if (data.error) {
          setLastNote(data.error);
          toast.error(data.error);
        } else {
          setLastNote(data.note || data.clicked || data.filled || data.selected || data.title || "OK");
        }
        await refresh();
        return data;
      } catch (e) {
        toast.error(apiError(e, "Navigateur indisponible"));
      } finally {
        setBusy(false);
      }
    },
    [refresh]
  );

  const goto = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    await run("goto", { url: url.trim() });
  };

  const close = async () => {
    setBusy(true);
    try {
      await api.post("/browser/close");
      setState({ active: false });
      setLastNote("Session fermée");
    } catch (_) {
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="browser-preview-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center sm:p-6"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        data-testid="browser-preview-panel"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl max-h-[88vh] overflow-y-auto no-scrollbar glass-card rounded-t-3xl sm:rounded-2xl p-4 animate-rise"
      >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Globe className="h-4 w-4 shrink-0 accent-text" />
          <span className="font-mono-hud text-[10px] uppercase tracking-[0.2em] text-[#8b949e]">Navigateur Sara</span>
          <span
            data-testid="browser-preview-status"
            className="shrink-0 rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase tracking-widest"
            style={
              state.active
                ? { color: "#00ffaa", background: "rgba(0,255,170,0.12)" }
                : { color: "#8b949e", background: "rgba(139,148,158,0.12)" }
            }
          >
            {state.active ? "En session" : "Inactif"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="browser-preview-refresh"
            onClick={refresh}
            className="hud-btn flex h-8 w-8 items-center justify-center clip-corner"
            title="Rafraîchir la capture"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            data-testid="browser-preview-close"
            onClick={onClose}
            className="hud-btn flex h-8 w-8 items-center justify-center clip-corner"
            title="Fermer l'aperçu"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <form onSubmit={goto} className="mb-3 flex gap-2">
        <input
          data-testid="browser-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={state.url || "https://exemple.com"}
          spellCheck={false}
          className="hud-input flex-1 font-mono text-[12px]"
        />
        <button
          type="submit"
          data-testid="browser-goto-button"
          disabled={busy}
          className="hud-btn clip-corner flex items-center gap-1.5 px-3 text-[11px] font-bold disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} Aller
        </button>
      </form>

      <div
        data-testid="browser-preview-screen"
        className="relative aspect-[16/10] w-full overflow-hidden clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.8)]"
      >
        {state.screenshot ? (
          <img src={state.screenshot} alt="Aperçu du navigateur de Sara" className="h-full w-full object-cover object-top" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <Globe className="h-6 w-6 text-[#3a4a5a]" />
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.18em] text-[#6b7a8d]">
              {state.active ? "Capture en cours…" : "Aucune session — ouvre une page ou demande-le à Sara"}
            </p>
          </div>
        )}
      </div>

      {(state.url || lastNote) && (
        <div className="mt-2 space-y-1">
          {state.url && (
            <p data-testid="browser-current-url" className="truncate font-mono text-[11px] text-[#9fb2c6]">
              {state.title ? `${state.title} — ` : ""}
              {state.url}
            </p>
          )}
          {lastNote && (
            <p data-testid="browser-last-note" className="truncate font-mono-hud text-[10px] text-[#6b7a8d]">
              » {lastNote}
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <Pill testid="browser-scroll-down" onClick={() => run("scroll", { direction: "down" })} disabled={busy} icon={ArrowDown}>
          Défiler
        </Pill>
        <Pill testid="browser-back" onClick={() => run("back")} disabled={busy} icon={ArrowLeft}>
          Retour
        </Pill>
        <Pill testid="browser-read" onClick={() => run("read")} disabled={busy} icon={Type}>
          Relire
        </Pill>
        <Pill testid="browser-session-close" onClick={close} disabled={busy} icon={PowerOff}>
          Fermer la session
        </Pill>
        <button
          type="button"
          data-testid="browser-advanced-toggle"
          onClick={() => setAdvanced((v) => !v)}
          className="ml-auto flex items-center gap-1 font-mono-hud text-[10px] uppercase tracking-widest text-[#8b949e] transition-colors hover:text-white"
        >
          {advanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Actions
        </button>
      </div>

      {advanced && (
        <div className="mt-3 space-y-2 border-t border-[var(--border-hud)] pt-3">
          <input
            data-testid="browser-selector-input"
            value={selector}
            onChange={(e) => setSelector(e.target.value)}
            placeholder="Élément : texte du bouton ou sélecteur CSS"
            className="hud-input font-mono text-[12px]"
          />
          <input
            data-testid="browser-value-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Valeur à saisir (pour Remplir)"
            className="hud-input font-mono text-[12px]"
          />
          <div className="flex flex-wrap gap-1.5">
            <Pill
              testid="browser-click-button"
              onClick={() => selector.trim() && run("click", { selector: selector.trim() })}
              disabled={busy || !selector.trim()}
              icon={MousePointerClick}
            >
              Cliquer
            </Pill>
            <Pill
              testid="browser-fill-button"
              onClick={() => selector.trim() && run("fill", { selector: selector.trim(), value, submit: true })}
              disabled={busy || !selector.trim()}
              icon={Type}
            >
              Remplir
            </Pill>
            <Pill
              testid="browser-search-button"
              onClick={() => value.trim() && run("search", { query: value.trim() })}
              disabled={busy || !value.trim()}
              icon={Globe}
            >
              Rechercher sur le site
            </Pill>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function Pill({ children, onClick, disabled, icon: Icon, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 clip-corner border border-[var(--border-hud)] px-2.5 py-1.5 font-mono-hud text-[10px] uppercase tracking-widest text-[#8b949e] transition-colors hover:bg-[var(--accent-a10)] hover:text-white disabled:opacity-40"
    >
      <Icon className="h-3.5 w-3.5" /> {children}
    </button>
  );
}
